// Client share view — shared building blocks (ported from
// design-reference/share/client-ui.jsx to TSX, driven by the frozen snapshot).
// View-only everywhere. Brand accent is snapshot.brand.primary (applied as a
// --di-orange override on the app root), so these all read var(--di-orange).
import type { CSSProperties, ReactNode } from "react";
import { SEV_ORDER, SEVERITY } from "../app/defects/meta";
import type { Defect } from "../app/defects/types";
import { fmtR, remTotal, type RemItem } from "../app/remediation/data";
import type { ShareSnapshot } from "../worker/modules/share-types";

export const SvMeta = ({ children, color = "var(--di-subtle)", style }: { children?: ReactNode; color?: string; style?: CSSProperties }) => (
  <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.13em", ...style }}>{children}</span>
);

const IcEye = ({ size = 11, color = "currentColor" }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" />
  </svg>
);

// ── header ──────────────────────────────────────────────────────────────
export function ClientHeader({ snapshot }: { snapshot: ShareSnapshot }) {
  const { ctx, brand } = snapshot;
  const a = ctx.asset;
  const insLine = [ctx.site, a ? a.name + (a.ref ? " · " + a.ref : "") : null, ctx.inspection ? ctx.inspection.date : null].filter(Boolean).join(" · ");
  return (
    <header style={{ display: "flex", alignItems: "center", gap: 18, padding: "0 22px", height: 66, borderBottom: "1px solid var(--di-border)", background: "var(--di-dark)", flexShrink: 0 }}>
      {brand.logoUrl
        ? <img src={brand.logoUrl} alt={brand.name} style={{ height: 38, width: "auto", maxWidth: 220, display: "block", objectFit: "contain" }} />
        : <img src="/assets/di-lockup-dark.svg" alt="Digital Intelligence" style={{ height: 40, width: "auto", display: "block" }} />}
      <div style={{ flex: 1 }}></div>
      <div style={{ textAlign: "right", minWidth: 0 }}>
        <SvMeta color="var(--di-label)">Prepared for</SvMeta>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--di-text)", marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ctx.client || "Client"}</div>
      </div>
      <div style={{ width: 1, height: 34, background: "var(--di-border)", flexShrink: 0 }}></div>
      <div style={{ textAlign: "right", minWidth: 0 }}>
        <SvMeta color="var(--di-label)">Inspection</SvMeta>
        <div style={{ fontSize: 12.5, fontWeight: 500, color: "var(--di-muted)", marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{insLine}</div>
      </div>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "var(--font-mono)", fontSize: 8.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--di-muted)", background: "var(--di-ink)", border: "1px solid var(--di-border)", borderRadius: 6, padding: "5px 10px", flexShrink: 0 }}>
        <IcEye size={11} color="var(--di-subtle)" />View only
      </span>
    </header>);
}

// ── summary stats ─────────────────────────────────────────────────────────
function StatCard({ label, value, sub }: { label: string; value: ReactNode; sub: ReactNode }) {
  return (
    <div style={{ padding: "13px 16px", borderRadius: 12, border: "1px solid var(--di-border)", background: "var(--di-dark)", minWidth: 0 }}>
      <SvMeta color="var(--di-label)">{label}</SvMeta>
      <div style={{ fontSize: 21, fontWeight: 600, color: "var(--di-text)", letterSpacing: "-0.02em", marginTop: 8, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
      <div style={{ fontSize: 11.5, color: "var(--di-faint)", marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</div>
    </div>);
}

export function SummaryStats({ snapshot, defects, remItems }: { snapshot: ShareSnapshot; defects: Defect[]; remItems: RemItem[] }) {
  const hasViewer = snapshot.orthos.length > 0 || !!snapshot.model3d;
  const reports = snapshot.reports ?? [];
  const cards: ReactNode[] = [];

  if (hasViewer) {
    const sevCounts: Record<string, number> = {};
    defects.forEach((d) => { sevCounts[d.severity] = (sevCounts[d.severity] || 0) + 1; });
    const parts = SEV_ORDER.filter((s) => sevCounts[s]);
    cards.push(<StatCard key="d" label="Defects" value={defects.length}
      sub={defects.length === 0 ? "None recorded" : parts.map((s, i) => (
        <span key={s}><span style={{ color: SEVERITY[s].color }}>{sevCounts[s]} {SEVERITY[s].label.toLowerCase()}</span>{i < parts.length - 1 ? " · " : ""}</span>
      ))} />);
  }
  if (snapshot.remediation) {
    const done = remItems.filter((it) => it.status === "complete").length;
    const total = remItems.reduce((a, it) => a + remTotal(it), 0);
    cards.push(<StatCard key="r" label="Work items" value={remItems.length} sub={remItems.length ? `${remItems.length - done} open · ${done} complete` : "None yet"} />);
    cards.push(<StatCard key="c" label="Estimated repairs" value={fmtR(total)} sub={total ? "excl. VAT" : "Awaiting estimates"} />);
  }
  if (reports.length) {
    cards.push(<StatCard key="p" label="Reports" value={reports.length} sub={"Latest " + reports[0]!.date} />);
  }
  if (!cards.length) return null;
  return <div style={{ display: "grid", gridTemplateColumns: `repeat(${cards.length}, 1fr)`, gap: 10 }}>{cards}</div>;
}

// ── section header (briefing layout) ──────────────────────────────────────
export function SvSection({ label, title, sub, children }: { label: string; title: string; sub?: string; children?: ReactNode }) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div>
        <SvMeta color="var(--di-orange)">{label}</SvMeta>
        <h2 style={{ fontSize: 19, fontWeight: 600, color: "var(--di-text)", letterSpacing: "-0.02em", marginTop: 6 }}>{title}</h2>
        {sub && <p style={{ fontSize: 13, color: "var(--di-subtle)", lineHeight: 1.6, marginTop: 5, maxWidth: 640 }}>{sub}</p>}
      </div>
      {children}
    </section>);
}

// ── footer ────────────────────────────────────────────────────────────────
const fmtSvDate = (ts: number) => new Date(ts).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
export function ShareFooter({ createdAt }: { createdAt: number }) {
  return (
    <footer style={{ padding: "22px 22px 30px", display: "flex", flexDirection: "column", alignItems: "center", gap: 9, textAlign: "center" }}>
      <SvMeta color="var(--di-label)">Shared by Digital Intelligence · dig-int.ai</SvMeta>
      <p style={{ fontSize: 11.5, color: "var(--di-faint)", lineHeight: 1.6, maxWidth: 620 }}>
        This link provides view-only access to the findings of an aerial inspection{createdAt ? ", shared on " + fmtSvDate(createdAt) : ""}. Findings record visible surface conditions only and do not constitute a structural engineering assessment. Cost estimates are indicative and exclude VAT.
      </p>
    </footer>);
}
